import { useQuery, useMutation } from '@tanstack/react-query'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { toast } from 'sonner'
import { useAuthStore } from '../../store/authStore'
import { userApi, notificationsApi } from '../../services/api'
import { UserData } from '@/app/modules/auth/models/UserModel'

// ============================================
// USER TYPES
// ============================================

export interface UpdateProfileRequest {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  occupation?: string
  pic?: string
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
  new_password_confirm: string
}

export interface RegisterFCMRequest {
  fcm_token: string
  device_type?: string
}

export interface UserPermission {
  id: string
  name: string
  codename: string
}

// ============================================
// USER PROFILE QUERIES
// ============================================

/**
 * Hook for fetching the current user profile
 * Automatically syncs user data to Zustand store
 */
export function useUserProfile() {
  // Don't fetch user profile on auth pages (login, signup, forgot-password, etc.)
  const isAuthPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth')

  return useQuery({
    queryKey: queryKeys.user.profile,
    queryFn: async (): Promise<any> => {
      const data = await userApi.getProfile()

      // Sync to Zustand store
      useAuthStore.getState().UserLoaded(data)

      return data
    },
    enabled: !isAuthPage, // Only fetch when not on auth pages
    staleTime: QUERY_CONFIG.USER_PERMISSIONS.staleTime,
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 1
    },
  })
}
export function useUserData() {
  // Don't fetch user profile on auth pages (login, signup, forgot-password, etc.)
  const isAuthPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth')

  return useQuery({
    queryKey: queryKeys.userProfile.profile,
    queryFn: async (): Promise<UserData> => {
      const data = await userApi.getUserData()

      // Sync to Zustand store
      useAuthStore.getState().UserProfile(data)

      return data
    },
    enabled: !isAuthPage,
    staleTime: QUERY_CONFIG.USER_PERMISSIONS.staleTime,
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 1
    },
  })
}

/**
 * Hook for fetching user permissions
 * Auto-refetch timing controlled via environment variables
 */
export function useUserPermissions() {
  // Don't fetch permissions on auth pages (login, signup, forgot-password, etc.)
  const isAuthPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth')
  // Subscribe to the active org. Permissions are org-scoped on the backend and
  // the X-Current-Organization header is only attached once orgId is truthy.
  const orgId = useAuthStore((state) => state.orgId)

  return useQuery({
    // Scope the cache to the org so a wrong/empty result can't leak across orgs
    // and the query self-corrects (refetches) when the org loads or changes.
    queryKey: [...queryKeys.user.permissions, orgId],
    queryFn: () => userApi.getPermissions(),
    // Don't fetch until we know which org we're in: this guarantees the
    // X-Current-Organization header is attached, so the backend never has to
    // guess and silently return an empty permission list.
    enabled: !isAuthPage && !!orgId, // Only fetch when not on auth pages and the org is known
    staleTime: QUERY_CONFIG.USER_PERMISSIONS.staleTime,
    refetchInterval: (query) => {
      // Stop refetching if there's an error (e.g., 401)
      if (query.state.error) {
        return false
      }
      return QUERY_CONFIG.USER_PERMISSIONS.refetchInterval
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 1
    },
  })
}

// ============================================
// USER PROFILE MUTATIONS
// ============================================

/**
 * Hook for updating user profile
 */
export function useUpdateProfile() {
  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => userApi.updateProfile(data as any),
    onSuccess: () => {
      // Invalidate user profile to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile })

      // Also invalidate organizations as user data might affect org list
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })

      toast.success('Profile updated successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for changing password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) =>
      userApi.changePassword({
        old_password: data.current_password,
        new_password: data.new_password,
      }),
    onSuccess: () => {
      toast.success('Password changed successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for registering FCM token for push notifications
 * This is a fire-and-forget mutation
 */
export function useRegisterFCM() {
  return useMutation({
    mutationFn: (data: RegisterFCMRequest) =>
      notificationsApi.registerFCMToken({
        registration_id: data.fcm_token,
        type: (data.device_type as 'web' | 'android' | 'ios') || 'web',
      }),
    // Don't show success/error notifications for FCM
    onError: () => {
      // Silent failure for FCM registration
    },
  })
}
